import { useEffect, useRef, useState } from 'react';
import './searchForm.sass';

const MOBILE_QUERY = '(max-width: 850px)';
const isMobileViewport = () => window.matchMedia?.(MOBILE_QUERY).matches ?? false;

// A nivel de módulo porque en la primera búsqueda el formulario de la portada se desmonta y
// el que queda abajo es otra instancia: tiene que saber que la pregunta salió de un formulario.
let focusAfterSearch = false;

/** Elementos donde Enter ya tiene un uso propio (escribir, activar un botón o un enlace). */
function isInteractiveTarget(target) {
  return target instanceof Element
    && !!target.closest('input, textarea, select, button, a[href], [contenteditable="true"], [role="button"]');
}

/**
 * Formulario de búsqueda nativo de React.
 * Mantiene el texto como un borrador local para que, después de enviar una
 * consulta, el campo vuelva a quedar vacío como en la integración de WordPress.
 */
export default function SearchForm({ onSubmit, isSearching = false, isSearchActive = false }) {
  const [text, setText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const wasSearchingRef = useRef(isSearching);

  // El textarea se deshabilita mientras el agente responde y con eso pierde el foco: al
  // terminar se devuelve (sin mover la página) si la pregunta salió de este formulario.
  // En móvil no, para no abrir el teclado sin que la persona lo pida.
  useEffect(() => {
    const finished = wasSearchingRef.current && !isSearching;
    wasSearchingRef.current = isSearching;
    if (!finished || !focusAfterSearch) return;

    focusAfterSearch = false;
    if (!isMobileViewport()) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [isSearching]);

  // Enter fuera de un campo lleva al input (y lo trae a la vista si quedó abajo).
  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      if (event.key !== 'Enter' || event.shiftKey || event.isComposing || event.defaultPrevented) return;
      if (isSearching || isInteractiveTarget(event.target)) return;

      const input = inputRef.current;
      if (!input) return;

      event.preventDefault();
      input.focus({ preventScroll: true });

      const { top, bottom } = input.getBoundingClientRect();
      if (top < 0 || bottom > window.innerHeight) {
        input.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isSearching]);

  const handleClear = () => {
    setText('');
    inputRef.current?.focus();
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const searchQuery = text.trim();
    if (!searchQuery || isSearching) {
      return;
    }

    focusAfterSearch = true;
    onSubmit(searchQuery);
    setText('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !isMobileViewport()) {
      handleSubmit(event);
      return;
    }

    if (event.key === 'Escape') {
      handleClear();
    }
  };

  return (
    <section
      className={`amx-search-fixed-container ${isSearchActive ? 'search-active' : ''}`}
    >
      <div className="container">
        <form
          className={`amx-search-form ${isFocused ? 'amx-search_focused' : ''} ${isSearching ? 'searching' : ''}`}
          onSubmit={handleSubmit}
        >
          <div className="amx-search-input-container">
            <div className="dots_border" aria-hidden="true" />
            <textarea
              ref={inputRef}
              id="query"
              className="amx-search-input"
              name="q"
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Haz una pregunta"
              autoComplete="off"
              autoCapitalize="off"
              enterKeyHint="enter"
              maxLength={8192}
              style={{ height: '24px' }}
              disabled={isSearching}
              aria-busy={isSearching}
            />

            <div className="amx-search-actions">
              {text && !isSearching && (
                <button
                  type="button"
                  className="buscador_search-btn search-clear-button"
                  onClick={handleClear}
                  aria-label="Limpiar texto"
                  title="Limpiar texto"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              )}

              <button
                type="submit"
                id="buscarBtn"
                className={`buscador_search-btn search-button search-btn-utv`}
                aria-label="Buscar"
                title="Buscar"
                disabled={isSearching || !text.trim()}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12l7-7 7 7" />
                </svg>
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};